/**
 * Curved Row Utility Functions for Konva-based Seating Display
 *
 * This module provides utilities for calculating curved seat positions using quadratic Bezier curves
 * with optimal spacing algorithms to display curved seating rows.
 */

export interface CurvePoints {
  start: { x: number; y: number };
  control: { x: number; y: number };
  end: { x: number; y: number };
}

export interface CurvedRowDisplay {
  curveEnabled: boolean;
  curvePoints?: CurvePoints;
  invertSeatOrder?: boolean;
}

export interface CurvedSeatPositionParams {
  seatIndex: number;
  totalSeats: number;
  curvePoints: CurvePoints;
  spacing: number;
  useOptimalSpacing?: boolean;
  invertSeatOrder?: boolean;
}

/**
 * Get a point on a quadratic Bezier curve at parameter t
 * @param curvePoints - Object with start, control, and end points
 * @param t - Parameter value (0-1)
 * @returns Point with x and y coordinates
 */
export const getBezierPoint = (
  curvePoints: CurvePoints,
  t: number
): { x: number; y: number } => {
  const { start, control, end } = curvePoints;
  const x =
    (1 - t) ** 2 * start.x + 2 * (1 - t) * t * control.x + t ** 2 * end.x;
  const y =
    (1 - t) ** 2 * start.y + 2 * (1 - t) * t * control.y + t ** 2 * end.y;
  return { x, y };
};

/**
 * Calculate the length of a quadratic Bezier curve between two parameter values
 * @param curvePoints - Object with start, control, and end points
 * @param t1 - Start parameter (0-1)
 * @param t2 - End parameter (0-1)
 * @returns Length of the curve segment
 */
export const calculateCurveLength = (
  curvePoints: CurvePoints,
  t1: number,
  t2: number
): number => {
  const steps = 100;
  let length = 0;
  let prevPoint = getBezierPoint(curvePoints, t1);

  for (let i = 1; i <= steps; i++) {
    const t = t1 + (t2 - t1) * (i / steps);
    const currentPoint = getBezierPoint(curvePoints, t);
    const dx = currentPoint.x - prevPoint.x;
    const dy = currentPoint.y - prevPoint.y;
    length += Math.sqrt(dx * dx + dy * dy);
    prevPoint = currentPoint;
  }

  return length;
};

/**
 * Find the parameter t that corresponds to a specific distance along the curve
 * @param curvePoints - Object with start, control, and end points
 * @param targetDistance - Target distance from start of curve
 * @param totalLength - Total length of the curve
 * @returns Parameter t (0-1)
 */
export const findParameterForDistance = (
  curvePoints: CurvePoints,
  targetDistance: number,
  totalLength: number
): number => {
  if (targetDistance <= 0) {
    return 0;
  }
  if (targetDistance >= totalLength) {
    return 1;
  }

  // Binary search for the parameter that gives us the target distance
  let low = 0;
  let high = 1;
  const tolerance = 0.001;

  while (high - low > tolerance) {
    const mid = (low + high) / 2;
    const lengthAtMid = calculateCurveLength(curvePoints, 0, mid);

    if (lengthAtMid < targetDistance) {
      low = mid;
    } else {
      high = mid;
    }
  }

  return (low + high) / 2;
};

/**
 * Calculate optimal seat spacing for curved rows using arc-length parameterization
 * @param curvePoints - Object with start, control, and end points
 * @param totalSeats - Number of seats in the row
 * @param minSpacing - Minimum spacing between seats
 * @returns Array of t parameters for seat positions
 */
export const calculateOptimalSeatSpacing = (
  curvePoints: CurvePoints,
  totalSeats: number,
  minSpacing: number
): number[] => {
  if (totalSeats <= 1) {
    return [0];
  }
  if (totalSeats === 2) {
    return [0, 1];
  }

  const totalCurveLength = calculateCurveLength(curvePoints, 0, 1);
  const minTotalSpacing = minSpacing * (totalSeats - 1);

  // If the curve is too short for minimum spacing, use equal parametric distribution
  if (totalCurveLength < minTotalSpacing) {
    const positions: number[] = [];
    for (let i = 0; i < totalSeats; i++) {
      positions.push(i / (totalSeats - 1));
    }
    return positions;
  }

  // Calculate actual spacing to use (may be larger than minimum)
  const actualSpacing = totalCurveLength / (totalSeats - 1);

  // Calculate positions using arc-length parameterization
  const positions: number[] = [];
  for (let i = 0; i < totalSeats; i++) {
    if (i === 0) {
      positions.push(0);
    } else if (i === totalSeats - 1) {
      positions.push(1);
    } else {
      const targetDistance = actualSpacing * i;
      const t = findParameterForDistance(
        curvePoints,
        targetDistance,
        totalCurveLength
      );
      positions.push(t);
    }
  }

  return positions;
};

/**
 * Calculate curved seat position using quadratic Bezier curve
 * @param params - Object containing all positioning parameters
 * @returns Object with x and y coordinates
 */
export const calculateCurvedSeatPosition = (
  params: CurvedSeatPositionParams
): { x: number; y: number } => {
  const {
    seatIndex,
    totalSeats,
    curvePoints,
    spacing,
    useOptimalSpacing = false,
    invertSeatOrder = false,
  } = params;

  // Calculate adjusted seat index once at the top - applies seat order inversion if enabled
  const adjustedSeatIndex = invertSeatOrder
    ? totalSeats - 1 - seatIndex
    : seatIndex;

  if (
    !curvePoints ||
    !curvePoints.start ||
    !curvePoints.control ||
    !curvePoints.end
  ) {
    // Fallback to straight line positioning
    return {
      x: spacing * (adjustedSeatIndex + (1 - totalSeats) / 2),
      y: 0,
    };
  }

  let t: number;
  if (useOptimalSpacing && totalSeats > 1) {
    // Use arc-length parameterization for optimal spacing (more complex but better distribution)
    const seatPositions = calculateOptimalSeatSpacing(
      curvePoints,
      totalSeats,
      spacing
    );
    t = seatPositions[adjustedSeatIndex];
  } else {
    // Simple parametric distribution
    t = totalSeats > 1 ? adjustedSeatIndex / (totalSeats - 1) : 0.5;
  }

  // Quadratic Bezier curve formula: B(t) = (1-t)²P₀ + 2(1-t)tP₁ + t²P₂
  const { start, control, end } = curvePoints;
  const x =
    (1 - t) ** 2 * start.x + 2 * (1 - t) * t * control.x + t ** 2 * end.x;
  const y =
    (1 - t) ** 2 * start.y + 2 * (1 - t) * t * control.y + t ** 2 * end.y;

  return { x, y };
};
